import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { useTranslation } from '@/src/i18n';
import { useImageUpload } from '@/src/presentation/hooks/useImageUpload';
import { EditProfileScreen } from '@/src/presentation/screens/EditProfileScreen';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';
import { updateProfileUseCase } from '@/src/core/dependencies';
import { ModerationError } from '@/src/domain/models/Moderation';

export default function EditProfileContainer() {
  const { user } = useAuth();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();

  const [fullName, setFullName] = useState(user?.user_metadata?.full_name || '');
  const [bio, setBio] = useState(user?.user_metadata?.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.user_metadata?.avatar_url || '');
  const [loading, setLoading] = useState(false);

  const { pickAndUploadImage, uploading } = useImageUpload({
    bucket: 'avatars',
    userId: user?.id || '',
    preset: 'avatar',
    fieldType: 'avatar_image',
    onSuccess: (url) => {
      setAvatarUrl(url);
      platformAlert(t('common.success'), t('alerts.photoUploaded'));
    },
  });

  const handleSave = async () => {
    if (!user) return;

    try {
      setLoading(true);

      await updateProfileUseCase.execute({
        userId: user.id,
        fullName,
        bio,
        avatarUrl,
      });

      platformAlert(t('common.success'), t('alerts.profileUpdated'), [
        {
          text: t('common.ok'),
          onPress: () => router.push(`/profile/${user.id}`),
        },
      ]);
    } catch (error) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else if (error instanceof Error && error.message === 'User not authenticated') {
        return;
      } else {
        logger.error('EditProfile', 'Failed to update profile', { error });
        platformAlert(t('common.error'), t('errors.failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    router.push(`/profile/${user?.id}`);
  };

  const getInitials = () => {
    if (!user?.email) return '?';
    return user.email.substring(0, 2).toUpperCase();
  };

  return (
    <EditProfileScreen
      fullName={fullName}
      setFullName={setFullName}
      bio={bio}
      setBio={setBio}
      avatarUrl={avatarUrl}
      email={user?.email || ''}
      loading={loading}
      uploading={uploading}
      getInitials={getInitials}
      handleSave={handleSave}
      handleCancel={handleCancel}
      pickImage={pickAndUploadImage}
      colors={colors}
    />
  );
}
